import {
	CloseCircleOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
	HistoryOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import {
	Badge,
	Button,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Timeline,
	Tooltip,
} from 'antd';
import Title from 'antd/es/typography/Title';
import { loanRequestApi } from 'Api/loanRequest';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Paid, PendingForApproval } from 'utils/svgs';
import {
	decrypt,
	formatNumber,
	invalidText,
	isIzbEnabledForCompany,
	toastText,
} from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
const { Column } = Table;

type BankDetail = {
	id: string;
	name: string;
	balance: number;
};

type PaymentHistory = {
	id: string;
	status: string;
	message: string;
	timestamp: string;
	attemptNumber: number;
	initiatedBy: string;
	amount: number;
	apiRequestPayload?: any;
	apiResponsePayload?: any;
};

type PaymentLog = {
	id: string;
	loanId: string;
	employeeId: string;
	employeeName: string;
	employeeCode: string;
	status: string;
	message: string;
	timestamp: string;
	amount: number;
	retryCount: number;
	paymentHistory: PaymentHistory[];
	hasHistory: boolean;
	totalAttempts: number;
	isUSD?: boolean;
	fileType?: string;
	bankAccountNumber?: string;
};

type Props = {
	isOpen: boolean;
	handleCancel: () => void;
	loanId: string;
	isBankLoading?: boolean;
	bankDetailsData?: BankDetail[];
	updateMidMonthStatus?: (loanId: string, status: string) => void;
};

const LogsModal = ({ isOpen, handleCancel, loanId }: Props) => {
	const [isLogsLoading, setIsLogsLoading] = useState(false);
	const [paymentLogsData, setPaymentLogsData] = useState<PaymentLog[]>([]);
	const [sortField, setSortField] = useState('createdAt');
	const [sortOrder, setSortOrder] = useState('desc');
	const [bankType, setBankType] = useState<'FCB' | 'STANBIC' | 'IZB'>('FCB');
	const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
	const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState<any[]>(
		[],
	);
	const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
	const [selectedEmployeeCode, setSelectedEmployeeCode] = useState('');

	const { data } = useSelector((state: any) => state?.userProfile);
	const encryptionKey: any = process.env.REACT_APP_ENCRYPTION_KEY;

	useEffect(() => {
		if (isOpen && loanId) {
			fetchPaymentLogs();
		}
	}, [isOpen, loanId, sortField, sortOrder, bankType]);

	const fetchPaymentLogs = async () => {
		try {
			setIsLogsLoading(true);

			const query = {
				loanId,
				bankType,
				sortBy: sortField,
				sortOrder,
			};

			const response = await loanRequestApi.getPaymentLogs(query);

			const logs = response?.data?.paymentLogs || [];
			setPaymentLogsData(logs);

			if (logs.length === 0) {
				toastText('No payment logs found for this loan batch', 'info');
			}
		} catch (error: any) {
			let message = 'Failed to fetch payment logs';
			if (error.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
			setPaymentLogsData([]);
		} finally {
			setIsLogsLoading(false);
		}
	};

	const handleTableChange = (pagination: any, filters: any, sorter: any) => {
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order);
		setSortField(sorter.field);
	};

	const handleBankType = (value: 'FCB' | 'STANBIC' | 'IZB') => {
		setBankType(value);
	};

	const handleHistoryCancel = () => {
		setIsHistoryModalVisible(false);
	};

	const getStatusDetails = (status: string) => {
		let statusText = 'No status';
		let statusClass = styles['status-placeholder'];
		let StatusIcon = <ExclamationCircleOutlined />;
		let color = 'default';

		switch (status?.toUpperCase()) {
			case 'PENDING':
				statusText = 'Pending';
				statusClass = styles['status-payment-under-process'];
				StatusIcon = <PendingForApproval />;
				color = '#ef896e';
				break;

			case 'SUCCESS':
				statusText = 'Success';
				statusClass = styles['status-paid'];
				StatusIcon = <Paid />;
				color = '#019c00';
				break;

			case 'PROCESSING':
				statusText = 'Processing';
				statusClass = styles['status-processing'];
				StatusIcon = (
					<SyncOutlined style={{ color: '#2f89a6', fontSize: 16 }} />
				);
				color = '#2f89a6';
				break;

			case 'FAILED':
				statusText = 'Failed';
				statusClass = styles['status-rejected'];
				StatusIcon = (
					<CloseCircleOutlined style={{ color: '#ef726e', fontSize: 16 }} />
				);
				color = 'red';
				break;

			default:
				break;
		}

		return { statusText, statusClass, StatusIcon, color };
	};

	const handleHistoryClick = (record: PaymentLog) => {
		setSelectedEmployeeName(record.employeeName);
		setSelectedEmployeeCode(record.employeeCode);
		setSelectedEmployeeHistory(record.paymentHistory);
		setIsHistoryModalVisible(true);
	};

	return (
		<>
			<Modal
				open={isOpen}
				onCancel={handleCancel}
				closable={false}
				maskClosable={true}
				footer={false}
				title={
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Space size={12} align="center">
							<Title level={4} style={{ margin: 0 }}>
								Payment Logs
							</Title>
							<Select
								value={bankType}
								style={{ width: 170 }}
								options={[
									{
										label: 'First Capital Bank',
										value: 'FCB',
									},
									{
										label: 'Stanbic Bank',
										value: 'STANBIC',
									},
									...(isIzbEnabledForCompany()
										? [{ label: 'Indo Zambia Bank', value: 'IZB' }]
										: []),
								]}
								onChange={handleBankType}
								size="middle"
							/>
						</Space>
						<Button
							type="text"
							icon={<CloseOutlined />}
							onClick={handleCancel}
							style={{ fontSize: 16 }}
						/>
					</div>
				}
				width={1100}
			>
				<div className="log-table">
					<Table
						dataSource={paymentLogsData}
						rowKey="id"
						loading={isLogsLoading}
						onChange={handleTableChange}
						pagination={false}
						scroll={{ y: 'calc(70vh - 180px)' }}
					>
						<Column
							title="Employee Name | Code"
							dataIndex="employeeId"
							key="employeeId"
							width={250}
							fixed="left"
							className="bg-white"
							sorter={true}
							render={(text: string, record: any) => (
								<span>
									<span>{record.employeeName} | </span>
									<span style={{ color: '#584495' }}>
										{record?.employeeCode}
									</span>
								</span>
							)}
						/>
						<Column
							title="Amount"
							dataIndex="amount"
							key="amount"
							width={150}
							className="bg-white"
							sorter={bankType === 'STANBIC'}
							render={(value: string, record: any) => {
								if (record.isUSD) {
									return formatNumber(
										Number(value),
										data?.CompanyCurrencies,
										true,
									);
								} else {
									return formatNumber(Number(value), data?.CompanyCurrencies);
								}
							}}
						/>
						<Column
							title="Bank Account Number"
							key="accountNumber"
							className="bg-white"
							width={190}
							render={(_, record: any) => {
								// More defensive check
								const accountNumber =
									record?.accountNumber &&
									typeof record.accountNumber === 'string' &&
									record.accountNumber.trim() !== '' &&
									!invalidText(record.accountNumber)
										? decrypt(record.accountNumber, encryptionKey)
										: '';

								if (!accountNumber) {
									return <span>-</span>;
								} else {
									return accountNumber;
								}
							}}
						/>
						<Column
							title="Status"
							dataIndex="status"
							key="status"
							width={180}
							className={`${styles['statusColumn']} bg-white`}
							render={(value, record: any) => {
								const { statusText, statusClass, StatusIcon } =
									getStatusDetails(value);

								return (
									<div className={styles['statusContainer']}>
										<Tag
											bordered={false}
											className={`${styles['statusTag']} ${statusClass}`}
										>
											<div className={styles['statusIcon']}>
												{StatusIcon} <span>{statusText}</span>
											</div>
										</Tag>
										{record.retryCount > 0 && (
											<Tooltip title={`Retried ${record.retryCount} time(s)`}>
												<Badge
													count={record.retryCount}
													style={{
														backgroundColor: '#ff4d4f',
														marginLeft: 8,
													}}
												/>
											</Tooltip>
										)}
									</div>
								);
							}}
						/>
						{bankType === 'STANBIC' && (
							<Column
								title="File Type"
								dataIndex="fileType"
								key="fileType"
								className="bg-white"
								width={120}
								render={(value: string) => value || '-'}
							/>
						)}
						<Column
							title="Message"
							dataIndex="message"
							key="message"
							className="bg-white"
							width={220}
							ellipsis={{
								showTitle: false,
							}}
							render={(message: string) => (
								<Tooltip placement="topLeft" title={message}>
									{message || '-'}
								</Tooltip>
							)}
						/>
						<Column
							title="Payment Date, Time"
							dataIndex="timestamp"
							key="timestamp"
							className="bg-white"
							width={200}
							render={(value: string) =>
								value ? dayjs(value).format('DD/MM/YYYY, hh:mm A') : '-'
							}
						/>
						{(bankType === 'FCB' || bankType === 'IZB') && (
							<Column
								title="History"
								key="history"
								width={100}
								align="center"
								className="bg-white"
								render={(_, record: PaymentLog) => (
									<Tooltip
										title={
											record.hasHistory
												? `View ${record.totalAttempts} payment attempt(s)`
												: 'No payment history'
										}
									>
										<Button
											type="text"
											icon={<HistoryOutlined />}
											disabled={!record.hasHistory}
											onClick={() => handleHistoryClick(record)}
											style={{
												fontSize: '18px',
												color: record.hasHistory ? '#584495' : '#d9d9d9',
											}}
										/>
									</Tooltip>
								)}
							/>
						)}
					</Table>
				</div>
			</Modal>

			{/* Payment History Modal */}
			<Modal
				open={isHistoryModalVisible}
				onCancel={handleHistoryCancel}
				centered
				closable={false}
				maskClosable={true}
				footer={false}
				title={
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Title level={4} style={{ margin: 0 }}>
							Payment History
							<span style={{ fontSize: 16, color: '#666' }}>
								{' '}
								- {selectedEmployeeName} | {selectedEmployeeCode}
							</span>
						</Title>
						<Button
							type="text"
							icon={<CloseOutlined />}
							onClick={handleHistoryCancel}
							style={{ fontSize: 16 }}
						/>
					</div>
				}
				width={630}
			>
				<div
					style={{
						padding: '22px 0',
						maxHeight: '65vh', // control scroll height
						overflowY: 'auto',
						overflowX: 'hidden',
						paddingRight: 8, // prevent content cutoff due to scrollbar
					}}
				>
					<Timeline mode="left">
						{selectedEmployeeHistory.map((history, index) => {
							const { statusText, statusClass, StatusIcon } = getStatusDetails(
								history.status,
							);
							let fromAccount = '';
							let toAccount = '';

							if (history.apiRequestPayload) {
								const payload = JSON.parse(history.apiRequestPayload);
								fromAccount = payload?.fromAccountNo || '';
								toAccount = payload?.toAccountNo || '';
							}

							return (
								<Timeline.Item
									key={history.id}
									dot={StatusIcon}
									label={
										<div style={{ width: 180 }}>
											<div
												style={{
													fontWeight: 600,
													fontSize: 14,
												}}
											>
												Attempt #{history.attemptNumber}
											</div>
											<div
												style={{
													fontSize: 12,
													color: '#666',
													marginTop: 2,
												}}
											>
												{dayjs(history.timestamp).format('DD/MM/YYYY')}
											</div>
											<div
												style={{
													fontSize: 12,
													color: '#666',
												}}
											>
												{dayjs(history.timestamp).format('hh:mm A')}
											</div>
											<div
												style={{
													fontSize: 11,
													color: '#999',
													marginTop: 2,
												}}
											>
												<strong>By :</strong> {history.initiatedBy}
											</div>
										</div>
									}
								>
									<div style={{ marginBottom: 4 }}>
										<div className={styles['statusContainer']}>
											<Tag
												bordered={false}
												className={`${styles['statusTag']} ${statusClass}`}
											>
												<div className={styles['statusIcon']}>
													<span>{statusText}</span>
												</div>
											</Tag>
										</div>
									</div>
									<div
										style={{
											color: '#666',
											marginBottom: 2,
										}}
									>
										{history.message || 'No message available'}
									</div>
									{(fromAccount || toAccount) && (
										<div
											style={{
												fontSize: 12,
												color: '#999',
												marginTop: 3,
												paddingTop: 3,
											}}
										>
											{fromAccount && (
												<div>
													<strong>From : </strong> {fromAccount}
												</div>
											)}
											{toAccount && (
												<div>
													<strong>To :</strong> {toAccount}
												</div>
											)}
										</div>
									)}
									<div
										style={{
											fontSize: 12,
											color: '#999',
											marginTop: 2,
										}}
									>
										<strong> Amount : </strong>{' '}
										{formatNumber(
											Number(history.amount),
											data?.CompanyCurrencies,
										)}
									</div>
								</Timeline.Item>
							);
						})}
					</Timeline>
				</div>
			</Modal>
		</>
	);
};

export default LogsModal;
